import { useCallback, useState } from 'react';

const TASK_STATES = ['Pending', 'Executing', 'Awaiting Code Review', 'Completed'] as const;

interface TaskStatusDropdownProps {
  state: string;
  assignedToAI: boolean;
  workstreamID: string;
  taskID: string;
  version: number;
  onSaved?: (updatedFields: { State: string; Version: number; AssignedToAI: boolean }) => void;
}

export default function TaskStatusDropdown({
  state,
  assignedToAI,
  workstreamID,
  taskID,
  version,
  onSaved,
}: TaskStatusDropdownProps) {
  const [saving, setSaving] = useState(false);

  const handleChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newState = e.target.value;
      if (newState === state) return;

      setSaving(true);
      try {
        const fields: Record<string, unknown> = { State: newState };

        if (assignedToAI) {
          fields.AssignedToAI = false;
          fields.ClearAssignedToTenantID = true;
          fields.AutoPause = true;
        }

        const resp = await fetch(
          `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(taskID)}`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Version: version, Fields: fields }),
          },
        );
        if (resp.ok) {
          const data = await resp.json();
          onSaved?.({
            State: data.State,
            Version: data.Version,
            AssignedToAI: data.AssignedToAI ?? false,
          });
        }
      } catch {
        // silently fail
      } finally {
        setSaving(false);
      }
    },
    [state, assignedToAI, workstreamID, taskID, version, onSaved],
  );

  return (
    <select
      value={state}
      onChange={handleChange}
      disabled={saving}
      style={{
        padding: '4px 8px',
        fontSize: '14px',
        borderRadius: '6px',
        border: '1px solid #ccc',
        cursor: saving ? 'wait' : 'pointer',
      }}
    >
      {TASK_STATES.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}
